import React, { useState, useCallback } from 'react';
import { caseService } from '../../services/api';
import { toast } from 'sonner';
import { Loader2, FileSpreadsheet, X, AlertTriangle, UploadCloud } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FileDropzone } from '../ui/FileDropzone';

export interface CsvImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    preselectedCaseId?: string;
    /** When provided, shows "Add to current canvas" option. Called with selected addresses. */
    onAddToCanvas?: (addresses: ParsedAddress[]) => void;
}

export type { ParsedAddress };

interface ParsedAddress {
    address: string;
    chain: string;
    label: string | null;
}

const CHAIN_COLORS: Record<string, string> = {
    BTC: 'bg-orange-500/20 text-orange-400',
    ETH: 'bg-primary/20 text-primary',
    TRX: 'bg-red-500/20 text-red-400',
    SOL: 'bg-primary/15 text-foreground',
    BNB: 'bg-yellow-500/20 text-yellow-400',
    MATIC: 'bg-violet-500/20 text-violet-400',
};

/** Client-side chain detection — uses shared chainDetect util.
 *  For CSV imports we keep a single-string return; ambiguous EVM addresses
 *  default to ETH and can be re-classified by the chain_probe service later.
 */
import { detectChainStrict } from '../../utils/chainDetect';
function detectChain(addr: string): string {
    return detectChainStrict(addr) ?? 'UNKNOWN';
}

/** Accepted upload kinds (sheet #37): .csv is previewed here, .xlsx is parsed by the backend. */
export const IMPORT_ACCEPT = '.csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const IMPORT_UNSUPPORTED_MESSAGE = 'Only .csv and .xlsx files are supported';

export function importFileKind(name: string): 'csv' | 'xlsx' | null {
    const n = (name || '').trim().toLowerCase();
    if (n.endsWith('.csv')) return 'csv';
    if (n.endsWith('.xlsx')) return 'xlsx';
    return null;
}

/** Parse CSV text client-side for preview */
function parseCSVPreview(text: string): ParsedAddress[] {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const addrIdx = headers.findIndex(h => ['address', 'wallet', 'wallet_address'].includes(h));
    const chainIdx = headers.findIndex(h => ['chain', 'blockchain', 'network'].includes(h));
    const labelIdx = headers.findIndex(h => ['label', 'name'].includes(h));

    if (addrIdx === -1) return [];

    const results: ParsedAddress[] = [];
    const seen = new Set<string>();

    for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim());
        const addr = cols[addrIdx] || '';
        if (!addr) continue;

        let chain = chainIdx >= 0 ? (cols[chainIdx] || '').toUpperCase() : '';
        if (!chain || chain === 'UNKNOWN') {
            chain = detectChain(addr);
        }

        const key = `${chain}:${addr}`;
        if (seen.has(key)) continue;
        seen.add(key);

        results.push({
            address: addr,
            chain,
            label: labelIdx >= 0 ? cols[labelIdx] || null : null,
        });
    }
    return results;
}

export function CsvImportModal({ isOpen, onClose, preselectedCaseId, onAddToCanvas }: CsvImportModalProps) {
    const navigate = useNavigate();
    const [file, setFile] = useState<File | null>(null);
    const [isImporting, setIsImporting] = useState(false);
    // An .xlsx is sent to the backend as-is (no client-side preview).
    const [isSpreadsheet, setIsSpreadsheet] = useState(false);

    // Preview state
    const [previewAddresses, setPreviewAddresses] = useState<ParsedAddress[]>([]);
    const [selectedAddresses, setSelectedAddresses] = useState<Set<string>>(new Set());

    // Case mapping
    const [cases, setCases] = useState<any[]>([]);
    const [selectedCaseId, setSelectedCaseId] = useState(preselectedCaseId || '');
    const [newCaseTitle, setNewCaseTitle] = useState('');
    const [firNumber, setFirNumber] = useState('');
    const [importMode, setImportMode] = useState<'existing' | 'new' | 'canvas'>(
        onAddToCanvas ? 'canvas' : preselectedCaseId ? 'existing' : 'new'
    );
    const [investigationName, setInvestigationName] = useState('');

    const loadCases = useCallback(async () => {
        try {
            const data = await caseService.getAll();
            setCases(data);
            if (!selectedCaseId && data.length > 0) {
                setSelectedCaseId(data[0].id);
            }
        } catch {
            toast.error('Failed to load cases');
        }
    }, [selectedCaseId]);

    const processCsvFile = (selected: File) => {
        const kind = importFileKind(selected.name);
        if (!kind) {
            toast.error(IMPORT_UNSUPPORTED_MESSAGE);
            return;
        }

        setFile(selected);

        if (kind === 'xlsx') {
            // The workbook is parsed on the server (same endpoint, same
            // multipart field). Without a preview there is nothing to put on
            // the canvas directly, so the import goes into a case.
            setIsSpreadsheet(true);
            setPreviewAddresses([]);
            setSelectedAddresses(new Set());
            setInvestigationName(`Spreadsheet Import: ${selected.name}`);
            if (importMode === 'canvas') setImportMode(preselectedCaseId ? 'existing' : 'new');
            loadCases();
            return;
        }
        setIsSpreadsheet(false);

        // Read and preview
        const reader = new FileReader();
        reader.onload = (ev) => {
            const text = ev.target?.result as string;
            const parsed = parseCSVPreview(text);
            if (parsed.length === 0) {
                toast.error('No valid addresses found. Expected column header: address, wallet, or wallet_address');
                setFile(null);
                return;
            }
            setPreviewAddresses(parsed);
            setSelectedAddresses(new Set(parsed.map(a => `${a.chain}:${a.address}`)));
            setInvestigationName(`CSV Import: ${selected.name}`);
            loadCases();
        };
        reader.readAsText(selected);
    };

    const toggleAddress = (key: string) => {
        const next = new Set(selectedAddresses);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        setSelectedAddresses(next);
    };

    const toggleAll = () => {
        if (selectedAddresses.size === previewAddresses.length) {
            setSelectedAddresses(new Set());
        } else {
            setSelectedAddresses(new Set(previewAddresses.map(a => `${a.chain}:${a.address}`)));
        }
    };

    const handleImport = async () => {
        if (!isSpreadsheet && selectedAddresses.size === 0) {
            toast.error('Select at least one address');
            return;
        }

        // Canvas mode: add directly to canvas, no backend call
        if (importMode === 'canvas' && onAddToCanvas && !isSpreadsheet) {
            const selected = previewAddresses.filter(a => selectedAddresses.has(`${a.chain}:${a.address}`));
            onAddToCanvas(selected);
            toast.success(`Added ${selected.length} addresses to canvas`);
            handleClose();
            return;
        }

        if (importMode === 'existing' && !selectedCaseId) {
            toast.error('Select a case');
            return;
        }
        if (importMode === 'new' && !newCaseTitle.trim()) {
            toast.error('Enter a case title');
            return;
        }
        if (!file) return;

        setIsImporting(true);
        try {
            const result = await caseService.csvImport(file, {
                caseId: importMode === 'existing' ? selectedCaseId : undefined,
                caseTitle: importMode === 'new' ? newCaseTitle : undefined,
                firNumber: importMode === 'new' ? firNumber || undefined : undefined,
                investigationName: investigationName || undefined,
            });

            const caseId = result.case.id;
            const invId = result.investigation.id;
            const addrCount = result.addresses?.length || selectedAddresses.size;

            if (result.warnings?.length) {
                result.warnings.forEach((w: string) => toast.warning(w, { duration: 5000 }));
            }

            toast.success(`Imported ${addrCount} addresses into investigation`);
            onClose();
            navigate(`/workbench?case_id=${caseId}&investigation_id=${invId}`);
        } catch (e: any) {
            console.error('Address import failed', e);
            toast.error(e.response?.data?.detail || (isSpreadsheet ? 'Spreadsheet import failed' : 'CSV import failed'));
        } finally {
            setIsImporting(false);
        }
    };

    const resetState = () => {
        setFile(null);
        setIsSpreadsheet(false);
        setPreviewAddresses([]);
        setSelectedAddresses(new Set());
        setNewCaseTitle('');
        setFirNumber('');
        setInvestigationName('');
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    if (!isOpen) return null;

    const unknownChainCount = previewAddresses.filter(a => a.chain === 'UNKNOWN').length;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="w-full max-w-2xl rounded-xl flex flex-col max-h-[90vh] border border-border bg-card p-6 shadow-2xl">

                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-xl font-bold flex items-center gap-2 text-foreground">
                            <FileSpreadsheet className="w-5 h-5 text-green-400" />
                            Import Wallet Addresses (CSV / Excel)
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            Upload a .csv or .xlsx with wallet addresses. System auto-detects blockchain networks.
                        </p>
                    </div>
                    <button onClick={handleClose} className="text-muted-foreground hover:text-foreground p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Step 1: File Upload */}
                {previewAddresses.length === 0 && !isSpreadsheet && (
                    <div className="flex-1 flex flex-col items-center justify-center p-8">
                        <div className="w-full max-w-md space-y-4">
                            <div className="flex flex-col items-center text-center mb-2">
                                <FileSpreadsheet className="w-12 h-12 text-muted-foreground mb-4" />
                                <h4 className="text-foreground font-medium mb-2">Select a .csv or .xlsx file</h4>
                                <div className="text-xs max-w-sm mb-2 space-y-1.5">
                                    <p className="text-muted-foreground">
                                        <span className="inline-block bg-green-500/15 text-green-400 px-1.5 py-0.5 rounded font-medium text-[10px] mr-1">REQUIRED</span>
                                        <code className="text-green-400">address</code>
                                        <span className="text-muted-foreground/60 ml-1">(also accepts: <code>wallet</code>, <code>wallet_address</code>)</span>
                                    </p>
                                    <p className="text-muted-foreground">
                                        <span className="inline-block bg-muted px-1.5 py-0.5 rounded font-medium text-[10px] mr-1">OPTIONAL</span>
                                        <code>chain</code> <span className="text-muted-foreground/60">(auto-detected if missing)</span>,{' '}
                                        <code>label</code>
                                    </p>
                                </div>
                            </div>

                            <FileDropzone
                                bare
                                accept={IMPORT_ACCEPT}
                                file={file}
                                onFileChange={(f) => { if (f) processCsvFile(f); else setFile(null); }}
                                title="Wallet list"
                                description=".csv or .xlsx (first sheet, same column headers)"
                            />

                            {/* Example CSV hint */}
                            <div className="w-full bg-muted/50 p-3 rounded-lg border border-border/50">
                                <p className="text-[11px] text-muted-foreground font-medium mb-1">Example CSV format:</p>
                                <pre className="text-[10px] text-muted-foreground font-mono leading-relaxed">
{`address,chain,label
TWd4WrZ9wn84f5x1hZhL4DHvk738ns5jwb,TRX,WazirX Hot
0x28C6c06298d514Db089934071355E5743bf21d60,,Binance
bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh,,`}
                                </pre>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 2: Preview Table (CSV) or spreadsheet summary (.xlsx) */}
                {(previewAddresses.length > 0 || isSpreadsheet) && (
                    <div className="flex-1 flex flex-col min-h-0">
                        {isSpreadsheet ? (
                            <div className="flex items-start justify-between gap-3 mb-4 p-3 rounded-lg border border-border bg-muted/30" data-testid="xlsx-import-summary">
                                <div className="text-sm">
                                    <p className="text-foreground font-medium flex items-center gap-2">
                                        <FileSpreadsheet className="w-4 h-4 text-green-400" />
                                        {file?.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Excel workbook: the server reads the first sheet using the same column headers as a CSV
                                        (<code>address</code>, optional <code>chain</code>, <code>label</code>). No preview is shown;
                                        the import result lists what was read. Choose the case to import into.
                                    </p>
                                </div>
                                <button onClick={resetState} className="text-xs text-muted-foreground hover:text-foreground shrink-0">
                                    Change File
                                </button>
                            </div>
                        ) : (<>
                        <div className="flex items-center justify-between mb-3 border-b border-border pb-2">
                            <div className="flex items-center gap-3">
                                <h4 className="text-foreground font-medium text-sm">
                                    {selectedAddresses.size} / {previewAddresses.length} addresses selected
                                </h4>
                                {unknownChainCount > 0 && (
                                    <span className="flex items-center gap-1 text-[11px] text-amber-400">
                                        <AlertTriangle className="w-3 h-3" /> {unknownChainCount} unknown chain
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-3">
                                <button onClick={toggleAll} className="text-xs text-primary hover:underline">
                                    {selectedAddresses.size === previewAddresses.length ? 'Deselect All' : 'Select All'}
                                </button>
                                <button
                                    onClick={resetState}
                                    className="text-xs text-muted-foreground hover:text-foreground"
                                >
                                    Change File
                                </button>
                            </div>
                        </div>

                        {/* Address table */}
                        <div className="flex-1 overflow-y-auto mb-4 border border-border rounded-lg bg-card max-h-[300px]">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-card border-b border-border sticky top-0">
                                    <tr>
                                        <th className="p-2 w-10 text-center"></th>
                                        <th className="p-2 text-muted-foreground font-medium">Chain</th>
                                        <th className="p-2 text-muted-foreground font-medium">Address</th>
                                        <th className="p-2 text-muted-foreground font-medium">Label</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewAddresses.map((a, i) => {
                                        const key = `${a.chain}:${a.address}`;
                                        const isUnknown = a.chain === 'UNKNOWN';
                                        return (
                                            <tr
                                                key={i}
                                                onClick={() => !isUnknown && toggleAddress(key)}
                                                className={`border-b border-border/50 hover:bg-muted/30 ${isUnknown ? 'opacity-40' : 'cursor-pointer'}`}
                                            >
                                                <td className="p-2 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedAddresses.has(key)}
                                                        disabled={isUnknown}
                                                        onChange={() => {}}
                                                        className="w-4 h-4 rounded border-border/80 bg-muted text-primary focus:ring-blue-500"
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] ${
                                                        isUnknown
                                                            ? 'bg-secondary text-muted-foreground'
                                                            : CHAIN_COLORS[a.chain] || 'bg-slate-600/20 text-foreground/80'
                                                    }`}>
                                                        {a.chain}
                                                    </span>
                                                </td>
                                                <td className="p-2 font-mono text-xs text-foreground/80 truncate max-w-[280px]" title={a.address}>
                                                    {a.address}
                                                </td>
                                                <td className="p-2 text-xs text-muted-foreground truncate max-w-[120px]">
                                                    {a.label || '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        </>)}

                        {/* Case Mapping */}
                        <div className="bg-muted/40 p-4 rounded-lg border border-border/50 space-y-3">
                            {!preselectedCaseId && (
                                <div className="flex gap-4 border-b border-border/80 pb-3">
                                    {onAddToCanvas && !isSpreadsheet && (
                                        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                            <input
                                                type="radio"
                                                name="csv-case-dest"
                                                checked={importMode === 'canvas'}
                                                onChange={() => setImportMode('canvas')}
                                                className="text-primary bg-card border-border/80"
                                            />
                                            Add to Canvas
                                        </label>
                                    )}
                                    <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                        <input
                                            type="radio"
                                            name="csv-case-dest"
                                            checked={importMode === 'new'}
                                            onChange={() => setImportMode('new')}
                                            className="text-primary bg-card border-border/80"
                                        />
                                        New Case
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                        <input
                                            type="radio"
                                            name="csv-case-dest"
                                            checked={importMode === 'existing'}
                                            onChange={() => setImportMode('existing')}
                                            className="text-primary bg-card border-border/80"
                                        />
                                        Existing Case
                                    </label>
                                </div>
                            )}

                            {importMode === 'canvas' ? (
                                <p className="text-xs text-muted-foreground">Addresses will be added directly to the current Workbench canvas. No case will be created.</p>
                            ) : importMode === 'new' && !preselectedCaseId ? (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs text-muted-foreground mb-1">Case Title *</label>
                                        <input
                                            type="text"
                                            value={newCaseTitle}
                                            onChange={e => setNewCaseTitle(e.target.value)}
                                            placeholder="e.g. USDT Fraud Ring Investigation"
                                            className="w-full bg-card border border-border/80 rounded-md p-2 text-sm text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-muted-foreground mb-1">FIR Number</label>
                                        <input
                                            type="text"
                                            value={firNumber}
                                            onChange={e => setFirNumber(e.target.value)}
                                            placeholder="e.g. FIR-2026-042"
                                            className="w-full bg-card border border-border/80 rounded-md p-2 text-sm text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-xs text-muted-foreground mb-1">Select Case</label>
                                    <select
                                        value={selectedCaseId}
                                        onChange={e => setSelectedCaseId(e.target.value)}
                                        className="w-full bg-card border border-border/80 rounded-md p-2 text-sm text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                                        disabled={!!preselectedCaseId}
                                    >
                                        <option value="" disabled>-- Select a case --</option>
                                        {cases.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.title} {c.fir_number ? `(${c.fir_number})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-xs text-muted-foreground mb-1">Investigation Name</label>
                                <input
                                    type="text"
                                    value={investigationName}
                                    onChange={e => setInvestigationName(e.target.value)}
                                    placeholder="Auto-generated from filename"
                                    className="w-full bg-card border border-border/80 rounded-md p-2 text-sm text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-border">
                    <button
                        onClick={handleClose}
                        disabled={isImporting}
                        className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
                    >
                        Cancel
                    </button>

                    {(previewAddresses.length > 0 || isSpreadsheet) && (
                        <button
                            onClick={handleImport}
                            disabled={isImporting || (!isSpreadsheet && selectedAddresses.size === 0)}
                            className="px-5 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50 font-medium"
                        >
                            {isImporting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <UploadCloud className="w-4 h-4" />
                            )}
                            {isSpreadsheet ? 'Import Spreadsheet to Workbench' : `Import ${selectedAddresses.size} Addresses to Workbench`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
